import type {
	Certification,
	RecommendedCoursePlanItem,
} from "@brookbush/types";

import clsx from "clsx";
import { ArrowUpRightIcon } from "lucide-react";
import Link from "next/link";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";

import LandingPageCoursesCard from "./LandingPageCourseCard";
import SectionContainer from "~/components/SectionContainer";
import useRecommendedCoursePlan from "~/hooks/useRecommendedCoursePlan";
import {
	getLandingPageCertificationCredits,
	getRecommendedCoursePlanHref,
	isCptCertificationSlug,
} from "~/lib/landing-page-modules";
import fetcher from "~/utils/fetcher.client";

interface LandingPageModulesProps {
	certification: Certification & {
		recommendedCoursePlan: RecommendedCoursePlanItem[];
	};
}

const certifications = [
	{
		title: "Certified Personal Trainer",
		slug: "certified-personal-trainer",
		landingPageSlug: "certified-personal-trainer",
		description: "50 Credits",
		credits: 50,
		background: "bg-sky-50 dark:bg-sky-300/20",
	},
	{
		title: "Human Movement Specialist",
		description: "60 Credits",
		slug: "human-movement-specialist",
		credits: 60,
		landingPageSlug: "human-movement-specialist",
		background: "bg-amber-50 dark:bg-amber-200/20",
	},
	{
		title: "Integrated Manual Therapist",
		description: "80 Credits",
		credits: 80,
		background: "bg-purple-50 dark:bg-purple-200/20",
		slug: "integrated-manual-therapist-mt",
		landingPageSlug: "integrated-manual-therapist",
	},
	{
		title: "Strength and Performance Coach",
		description: "100 Credits",
		slug: "strength-and-performance-coach",
		credits: 100,
		landingPageSlug: "strength-and-performance-coach",
		background: "bg-red-50 dark:bg-red-200/20",
	},
];

const LandingPageModules = ({ certification }: LandingPageModulesProps) => {
	const [selectedCertification, setSelectedCertification] =
		useState(certification);

	const planSlug =
		getRecommendedCoursePlanHref(selectedCertification.slug).split("/")[2] ??
		selectedCertification.slug;

	const { recommendedCoursePlan } = useRecommendedCoursePlan(planSlug);

	const handleCertificationSelect = async (
		landingPageSlug: string,
		certSlug: string,
	) => {
		try {
			const [landingPage, fullCertification] = await Promise.all([
				fetcher(`/landing-pages/${landingPageSlug}?linkedCertification=true`),
				fetcher(`/certifications/${certSlug}`),
			]);
			setSelectedCertification({
				...landingPage.linkedCertification,
				slug: certSlug,
				title:
					fullCertification?.title ?? landingPage.linkedCertification.title,
				modules:
					fullCertification?.modules ??
					landingPage.linkedCertification.modules ??
					[],
			});
		} catch (_error) {
			toast.error("Failed to fetch certification data");
		}
	};

	useEffect(() => {
		if (!isCptCertificationSlug(certification.slug)) return;
		let cancelled = false;
		(async () => {
			try {
				const fullCertification = await fetcher(
					`/certifications/certified-personal-trainer`,
				);
				if (cancelled || !fullCertification?.modules?.length) return;
				setSelectedCertification((prev) => ({
					...prev,
					slug: "certified-personal-trainer",
					title: fullCertification.title ?? prev.title,
					modules: fullCertification.modules,
				}));
			} catch {
				// Keep SSR modules if refresh fails
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [certification.slug]);

	if (!selectedCertification?.modules?.length) return null;

	return (
		<SectionContainer className="!px-6 flex flex-col items-center gap-4 py-12">
			<h2 className="text-center font-bold text-2xl sm:text-4xl lg:text-4xl">
				{selectedCertification.title}
			</h2>

			<p className="mx-auto text-center text-lg lg:text-xl">
				This certification is worth{" "}
				{getLandingPageCertificationCredits(selectedCertification.slug)} credits
				and is broken down into the following modules:
			</p>

			<div className="flex flex-1 flex-col gap-6 sm:gap-10 lg:flex-row">
				{certifications.map((certification) => (
					<div
						className={clsx(
							"flex flex-col items-start justify-center gap-2 rounded-2xl px-6 py-4 hover:cursor-pointer hover:brightness-95 sm:px-5 sm:py-3 dark:hover:brightness-125",
							certification.background,
							selectedCertification.slug === certification.slug ||
								(isCptCertificationSlug(selectedCertification.slug) &&
									certification.slug === "certified-personal-trainer")
								? "border-2 border-sky-500 dark:border-sky-600"
								: undefined,
						)}
						key={certification.title}
						onClick={() =>
							handleCertificationSelect(
								certification.landingPageSlug,
								certification.slug,
							)
						}
					>
						<h3 className="font-bold text-lg sm:text-xl">
							{certification.title}
						</h3>
						<p className="text-base">{certification.description}</p>
					</div>
				))}
			</div>

			<div className="mt-4 flex w-full flex-col gap-8 lg:flex-row">
				<div className="flex w-full flex-row gap-8 lg:max-w-md">
					<div className="mx-auto flex w-full flex-col gap-4 rounded-md bg-slate-50 p-2 shadow-sm sm:p-3.5 lg:max-w-md dark:bg-slate-700">
						{selectedCertification &&
							selectedCertification.modules
								.filter((module) => module.title !== null)
								.map((module) => (
									<div
										key={module.title}
										className="flex flex-row items-center gap-4"
									>
										<p className="w-20 flex-shrink-0 rounded-full bg-sky-500 py-1 text-center text-sm text-white shadow-lg sm:text-sm dark:bg-sky-600">
											{module.credits} credits
										</p>
										<h3 className="flex-grow font-medium text-base drop-shadow-lg">
											{module.title}
										</h3>
									</div>
								))}
					</div>
				</div>

				<div className="w-full">
					{recommendedCoursePlan &&
						recommendedCoursePlan.length > 0 &&
						recommendedCoursePlan.slice(0, 3).map((item, catIdx) => {
							return (
								<div key={item.title} className="flex flex-col gap-2">
									<h3 className="mt-1 font-semibold text-xl">{item.title}</h3>
									<div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
										{item.courses.slice(0, 3).map((course, courseIdx) => {
											const isFirstRecommended =
												catIdx === 0 && courseIdx === 0;
											return (
												<LandingPageCoursesCard
													isFirstRecommended={isFirstRecommended ? true : false}
													key={course.title}
													{...course}
												/>
											);
										})}
									</div>
								</div>
							);
						})}

					<Link
						target="_blank"
						href={getRecommendedCoursePlanHref(selectedCertification.slug)}
						className="group col-span-full mx-auto mt-6 flex max-w-max items-center gap-1 rounded-full bg-slate-50 px-4 py-1.5 font-medium text-lg text-slate-800 hover:brightness-95 sm:gap-2 sm:px-5 sm:py-2 sm:text-xl dark:bg-slate-600 dark:text-white"
					>
						View Recommended Course Order{" "}
						<ArrowUpRightIcon className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
					</Link>
				</div>
			</div>
		</SectionContainer>
	);
};

export default LandingPageModules;
